import {
    useState,
    useCallback,
    useMemo,
    useEffect,
} from 'react'
import moment from 'moment'
import noop from 'lodash/noop'

import {
    dateToStrTimezone,
    getTimeZone,
    dateTimeZonedMask,
    dateToStr,
} from 'app/utils/date'
import {
    useAppSelector,
    useAppDispatch,
} from 'app/store/hooks'
import {
    toggleLocalTime,
    selectIsLocalTime,
} from 'app/store/Settings'
import {
    Event,
} from 'app/types/common.types'

import DateTimeZonedSelect from './DateTimeZonedSelect'
import {
    DateTimeZonedState,
} from './DateTimeZonedSelect.types'

type Props = {
    name?: string
    value?: string
    onChange?: (datetime: string, event: Event<string>) => void
    disabled?: boolean
    required?: boolean
    isDisableWeekends?: boolean
    defaultTimeZone?: string
    title?: string
    setErrors?: (errors: string[]) => void
    className?: string
}

const DateTimeZonedSelectContainer = ({
    name = '',
    value = null,
    onChange = noop,
    disabled = false,
    defaultTimeZone,
    isDisableWeekends = false,
    required,
    setErrors,
    className,
    title,
}: Readonly<Props>) => {
    const isLocalTime = useAppSelector(selectIsLocalTime)
    const dispatch = useAppDispatch()

    const timeZone = useMemo(() => {
        if (value) {
            return getTimeZone(value)
        }

        return defaultTimeZone || moment().format('ZZ')
    }, [
        defaultTimeZone,
        value,
    ])

    const [
        derivedTimeZone,
        setDerivedTimeZone,
    ] = useState(timeZone)

    const [
        state,
        setState,
    ] = useState<DateTimeZonedState>({
        open: false,
        newZone: timeZone,
        newDatetime: value,
    })

    const handleChangeZone = useCallback((newValue) => {
        setState({
            ...state,
            newZone: newValue,
        })
    }, [state])

    useEffect(() => {
        if (timeZone === derivedTimeZone) {
            return
        }

        setDerivedTimeZone(timeZone)
        handleChangeZone(timeZone)
    }, [
        derivedTimeZone,
        handleChangeZone,
        timeZone,
    ])

    const handleClose = useCallback(() => {
        setState({
            ...state,
            open: false,
        })
    }, [state])

    const showDate = useCallback((datetime) => {
        if (!datetime) {
            return ''
        }
        return isLocalTime
            ? dateToStr(
                moment(datetime, dateTimeZonedMask)
                    .utcOffset(moment().format('ZZ')),
            )
            : dateToStrTimezone(datetime, state.newZone)
    }, [
        isLocalTime,
        state.newZone,
    ])

    const setOpenDialog = useCallback(() => {
        if (!disabled) {
            setState((prev) => {
                return {
                    ...prev,
                    open: true,
                    newDatetime: prev.newDatetime || dateToStr(Date.now(), dateTimeZonedMask),
                }
            })
        }
    }, [disabled])

    const handleChange = useCallback((datetime) => {
        onChange(datetime, {
            target: {
                name,
                value: datetime,
            },
        })
    }, [
        name,
        onChange,
    ])

    const handleSetLocalTime = useCallback(() => {
        dispatch(toggleLocalTime())
    }, [dispatch])

    return (
        <DateTimeZonedSelect
            state={state}
            setState={setState}
            handleChangeZone={handleChangeZone}
            handleSetLocalTime={handleSetLocalTime}
            isLocalTime={isLocalTime}
            showDate={showDate}
            setOpenDialog={setOpenDialog}
            handleChange={handleChange}
            handleClose={handleClose}
            name={name}
            title={title}
            disabled={disabled}
            required={required}
            isDisableWeekends={isDisableWeekends}
            className={className}
            setErrors={setErrors}
            value={value}
        />
    )
}

export default DateTimeZonedSelectContainer
